import { useEffect, useMemo, useRef, useState } from 'react';
import { parseDelimited } from '@/utils/csv-helper';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Upload, Download, Scale, CheckCircle2, Filter, FileText, FileUp, Search } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';
import { Modal } from '@/components/ui/Modal';
import { DateRangePicker, rangeForPreset, DatePreset } from '@/components/ui/DateRangePicker';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';
import { CopyText } from '@/components/ui/CopyText';

interface Row {
  id: number; orderId: string; orderType: 'B2C'|'B2B';
  merchantName?: string | null;
  awbNo?: string; lrNo?: string; subAwb?: string;
  originalWeight: string; newWeight: string; diffWeight: string; volumetricWeight?: string;
  /** Released to the merchant by an internal reviewer. Merchants only ever receive released rows. */
  showToMerchant?: boolean;
  /** Server-side verdict: only a Delhivery capture can ever be released. */
  canShowToMerchant?: boolean;
  courierName?: string | null;
  weightType: 'DEAD' | 'VOLUMETRIC';
  source: 'UPLOAD' | 'TRACKING' | 'MANUAL';
  /** Who captured the row — resolved from `capturedById` by the API. Null for rows the system raised. */
  capturedByName?: string | null;
  reason?: string; isAccepted: boolean;
  capturedAt: string;
  status?: string;
  /** Who accepted / rejected it and when (internal screens; an auto-accept says so). */
  resolvedByName?: string | null;
  resolvedAt?: string | null;
}

/** Selectable for "Accept selected": still open — not accepted, auto-accepted or rejected. */
const isOpenRow = (r: Row) => (r.status === 'NEW' || r.status === 'PENDING') && !r.isAccepted;

/** "by Priya · 18 Oct, 6:40 pm" — the history line under a settled row. */
function ResolvedLine({ r }: { r: Row }) {
  if (!r.resolvedAt && !r.resolvedByName) return null;
  const when = r.resolvedAt ? new Date(r.resolvedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">
      {r.resolvedByName ? <>{r.status === 'AUTO_ACCEPTED' ? '' : 'by '}{r.resolvedByName}</> : null}
      {r.resolvedByName && when ? ' · ' : ''}{when}
    </div>
  );
}
interface Summary { count: number; totalKgDiff: number; avgKgDiff: number; accepted: number; pending: number; bySource: any[]; byType: any[] }

// ── Sample / template file ───────────────────────────────────────────────────
// Built in the browser (there is no server-side template endpoint for weight) but exposed from BOTH
// the page toolbar and the upload dialog, so nobody has to open the dialog to discover the format.
function sampleCsv(orderType: 'B2C' | 'B2B'): string {
  return orderType === 'B2C'
    ? `AWB,originalWeight,newWeight,volumetricWeight,weightType,reason\r
21194911524795,0.500,0.850,0.620,DEAD,Re-weighed at hub\r
21194911728742,1.200,1.900,1.750,VOLUMETRIC,Larger dimensions than booked`
    : `LR,subAwb,originalWeight,newWeight,volumetricWeight,weightType,reason\r
298043335,,5.000,6.200,5.800,DEAD,Re-weighed at hub\r
298043336,BOX-2,12.000,14.500,14.100,VOLUMETRIC,Larger dimensions than booked`;
}

function downloadSample(orderType: 'B2C' | 'B2B') {
  const blob = new Blob([sampleCsv(orderType)], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `weight-discrepancy-sample-${orderType.toLowerCase()}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(a.href);
}

export function WeightPage() {
  const qc = useQueryClient();
  const [orderType, setOrderType] = useState<'B2C' | 'B2B'>('B2C');
  const initial = rangeForPreset('this-month');
  const [start, setStart] = useState(initial.startDate);
  const [end, setEnd] = useState(initial.endDate);
  const [preset, setPreset] = useState<DatePreset>('this-month');
  const [source, setSource] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [reference, setReference] = useState('');
  const [disputing, setDisputing] = useState<Row | null>(null);
  const [rejecting, setRejecting] = useState<any | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  // '' any · '1' released to the merchant · '0' still internal. Internal screens only.
  const [shownFilter, setShownFilter] = useState('');
  const [merchantIds, setMerchantIds] = useState<(string | number)[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const { user: authUser } = useAuth();
  const isMerchant = authUser?.kind === 'merchant';

  const { data: merchants } = useQuery({
    enabled: !isMerchant,
    queryKey: ['merchants-light'],
    queryFn: async () => (await api.get('/merchants', { params: { pageSize: 500 } })).data,
  });
  const merchantList: any[] = Array.isArray(merchants) ? merchants : (merchants as any)?.items ?? [];

  // Every filter the list is drawn from, in one object — so the export can send exactly the same
  // set and can never disagree with what is on screen.
  // A weight discrepancy is raised by the courier days or weeks after booking, and the date range
  // here defaults to a recent window — so searching an AWB with the range still applied returned
  // nothing and read as "no discrepancy on this shipment", which is the opposite of the truth.
  // While a reference search is active the range is dropped and the UI says so.
  const searching = reference.trim().length > 0;

  const filterParams = useMemo(() => {
    const p: any = { orderType };
    if (!searching) { p.startDate = start; p.endDate = end; }
    if (source) p.source = source;
    if (statusFilter) p.status = statusFilter;
    if (searching) p.reference = reference.trim();
    if (!isMerchant && merchantIds.length) p.merchantIds = merchantIds.join(',');
    if (!isMerchant && shownFilter) p.showToMerchant = shownFilter;
    return p;
  }, [orderType, start, end, source, statusFilter, reference, searching, merchantIds, isMerchant, shownFilter]);

  const { data, isLoading } = useQuery({
    queryKey: ['weight', filterParams, page, pageSize],
    queryFn: async () =>
      (await api.get<{ total: number; items: Row[] }>('/weight', { params: { ...filterParams, page, pageSize } })).data,
  });
  const { data: sum } = useQuery({
    queryKey: ['weight-sum', { orderType, start, end }],
    queryFn: async () => (await api.get<Summary>('/weight/summary', { params: { orderType, startDate: start, endDate: end } })).data,
  });

  const total = data?.total ?? 0;

  const accept = useMutation({
    mutationFn: async (id: number) => (await api.post(`/weight/${id}/accept`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['weight'] }); qc.invalidateQueries({ queryKey: ['weight-sum'] }); toast.success('Accepted'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not accept'),
  });

  // Multi-select → Accept selected (internal staff / admin). Each row goes through the same accept
  // as a single click: who/when stamped, order weight changed, freight re-priced.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  useEffect(() => { setSelected(new Set()); }, [filterParams, page, pageSize]);
  const openRows = (data?.items ?? []).filter(isOpenRow);
  const allOpenSelected = openRows.length > 0 && openRows.every((r) => selected.has(r.id));
  const toggle = (id: number) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const acceptMany = useMutation({
    mutationFn: async (ids: number[]) => (await api.post('/weight/accept-bulk', { ids })).data,
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['weight'] }); qc.invalidateQueries({ queryKey: ['weight-sum'] });
      setSelected(new Set());
      if (r.accepted) toast.success(`Accepted ${r.accepted} of ${r.requested} — order weights and freight updated`);
      if (r.failed?.length) {
        toast.error(`${r.failed.length} not accepted: ${r.failed.slice(0, 5).map((f: any) => `${f.ref ?? '#' + f.id} — ${f.error}`).join(' · ')}${r.failed.length > 5 ? ' …' : ''}`, { duration: 12000 });
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not accept'),
  });

  /** Release a dispute to the merchant, or take it back. Internal only — the API refuses merchants. */
  const showToMerchant = useMutation({
    mutationFn: async (v: { ids: number[]; show: boolean }) => (await api.post('/weight/show-to-merchant', v)).data,
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['weight'] }); qc.invalidateQueries({ queryKey: ['weight-sum'] });
      toast.success(r?.showToMerchant ? `Released ${r.updated} to the merchant` : `${r?.updated ?? 0} kept internal`);
      if (r?.skippedReason) toast.warning(r.skippedReason);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not update'),
  });

  function exportCsv() {
    // Send the WHOLE filter set, not just the date range — the export used to ignore the source,
    // merchant and search filters and hand back rows the operator had filtered out on screen.
    const qs = new URLSearchParams(
      Object.entries(filterParams).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)]),
    );
    downloadBlob(`/weight/export?${qs.toString()}`, `weight-${orderType}-${new Date().toISOString().slice(0,10)}.csv`);
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Scale size={22} /> Weight Discrepancies</h1>
          <p className="text-sm text-slate-500">Differences between booked weight and actual weight (uploaded by ops or auto-detected during scans)</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50">
            <Download size={14} /> Export
          </button>
          {!isMerchant && <button onClick={() => downloadSample(orderType)} title={`Download the ${orderType} sample file with the exact column headers + example rows`}
            className="px-3 py-2 border border-indigo-200 text-indigo-700 rounded-lg text-sm flex items-center gap-1.5 hover:bg-indigo-50">
            <FileText size={14} /> Sample File
          </button>}
          {!isMerchant && <button onClick={() => setShowUpload(true)} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-primary/90">
            <Upload size={14} /> Import File
          </button>}
        </div>
      </div>

      {/* B2C / B2B tabs */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 mb-4 w-fit">
        {(['B2C','B2B'] as const).map(t => (
          <button key={t} onClick={() => setOrderType(t)}
            className={`px-5 py-1.5 text-sm rounded-md transition ${orderType === t ? 'bg-white shadow-sm text-primary font-medium' : 'text-slate-600 hover:text-slate-900'}`}>{t}</button>
        ))}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <Card label="Orders affected" value={sum?.count ?? 0} accent="indigo" />
        <Card label="Total kg increase" value={`${(sum?.totalKgDiff ?? 0).toFixed(3)} kg`} accent="amber" />
        <Card label="Avg per shipment" value={`${(sum?.avgKgDiff ?? 0).toFixed(3)} kg`} accent="slate" />
        <Card label="Pending acceptance" value={sum?.pending ?? 0} accent="rose" />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <BreakdownCard title="By source" rows={(sum?.bySource ?? []).map(r => ({ label: r.source, count: r.count, kg: r.kg }))} />
        <BreakdownCard title="By weight type (Dead vs Volumetric)" rows={(sum?.byType ?? []).map(r => ({ label: r.type, count: r.count, kg: r.kg }))} />
      </div>

      {/* Filters — search, merchant, source, status and the date range all on ONE row.
          The two selects previously carried the shared `.input` class, which is `w-full`; on a flex
          item that resolves to 100% of the row, so each dropdown claimed a whole line to itself and
          wrapped below the search box. They now carry explicit widths instead. `flex-wrap` is kept
          only as the graceful fallback for a genuinely narrow window — it is not the normal case. */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 mb-4 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 border border-slate-200 rounded-lg overflow-hidden shrink-0">
          <span className="px-2.5 py-2 text-sm bg-slate-50 border-r border-slate-200 text-slate-600 whitespace-nowrap">
            {orderType === 'B2C' ? 'AWB' : 'LR / Sub-AWB'}
          </span>
          <input type="text" value={reference} onChange={(e) => { setReference(e.target.value); setPage(1); }}
            placeholder="Search by number…" className="px-2.5 py-2 text-sm outline-none w-44" />
        </div>
        {searching && <div className="text-[10px] text-amber-600 self-center whitespace-nowrap">Searching all dates — the date range is ignored</div>}
        {!isMerchant && (
          <div className="w-48 shrink-0">
            <MultiSelectFilter label="" placeholder="Merchants" values={merchantIds}
              onChange={(v) => { setMerchantIds(v); setPage(1); }}
              options={merchantList.map((m: any) => ({ value: m.id, label: (m.brandName || m.companyName) + ' (#' + m.id + ')' }))} />
          </div>
        )}
        <select value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }}
          className="w-36 shrink-0 px-2.5 py-2 rounded-lg border border-slate-300 bg-white text-sm outline-none focus:border-primary">
          <option value="">All sources</option>
          <option value="UPLOAD">Upload</option>
          <option value="TRACKING">Tracking scan</option>
          <option value="MANUAL">Manual</option>
        </select>
        {!isMerchant && (
          <select value={shownFilter} onChange={(e) => { setShownFilter(e.target.value); setPage(1); }}
            className="input py-2 text-sm" title="Has this dispute been released to the merchant?">
            <option value="">Show to merchant: any</option>
            <option value="1">Released — merchant can see</option>
            <option value="0">Internal only</option>
          </select>
        )}
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="w-40 shrink-0 px-2.5 py-2 rounded-lg border border-slate-300 bg-white text-sm outline-none focus:border-primary">
          <option value="">All statuses</option>
          <option value="NEW">New</option>
          <option value="PENDING">Disputed (pending)</option>
          <option value="ACCEPTED">Accepted</option>
          <option value="AUTO_ACCEPTED">Auto Accepted</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <div className="w-48 shrink-0">
          <DateRangePicker value={{ startDate: start, endDate: end }} defaultPreset={preset}
            onChange={(r) => { setStart(r.startDate); setEnd(r.endDate); setPreset(r.preset); setPage(1); }} />
        </div>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <button onClick={() => qc.invalidateQueries({ queryKey: ['weight'] })}
            className="px-3 py-2 border border-emerald-300 text-emerald-700 text-sm rounded-lg hover:bg-emerald-50 flex items-center gap-2">
            <Search size={14} /> Search
          </button>
          <button onClick={() => {
            setReference(''); setMerchantIds([]); setSource(''); setStatusFilter(''); setPage(1);
          }} className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50">Reset</button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-2 text-xs text-slate-500">
        <span className="flex items-center gap-3">
          <span><Filter size={12} className="inline mr-1" /> {(data?.total ?? 0).toLocaleString()} rows</span>
          {!isMerchant && selected.size > 0 && (
            <button
              onClick={() => { if (window.confirm(`Accept ${selected.size} weight discrepanc${selected.size === 1 ? 'y' : 'ies'}? Each order's weight changes to the new courier weight and its freight is re-priced now. Freight already on an invoice is not changed — the extra goes on a separate WEIGHT charge.`)) acceptMany.mutate([...selected]); }}
              disabled={acceptMany.isPending}
              className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium inline-flex items-center gap-1 hover:bg-emerald-700 disabled:opacity-50">
              {acceptMany.isPending ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Accept selected ({selected.size})
            </button>
          )}
          {!isMerchant && selected.size > 0 && (
            <button onClick={() => setSelected(new Set())} className="text-xs text-slate-500 hover:underline">Clear</button>
          )}
        </span>
        <span>Rows per page:&nbsp;
          <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="text-xs border border-slate-200 rounded px-1 py-0.5">
            {[25, 50, 100, 200, 500].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          &nbsp;&nbsp;{total === 0 ? 0 : ((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}&nbsp;&nbsp;
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-1 disabled:opacity-30">‹</button>
          <button disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)} className="px-1 disabled:opacity-30">›</button>
        </span>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                {!isMerchant && (
                  <th className="px-3 py-2.5 w-8">
                    <input type="checkbox" checked={allOpenSelected} disabled={!openRows.length}
                      title="Select every open discrepancy on this page"
                      onChange={() => setSelected(allOpenSelected ? new Set() : new Set(openRows.map((r) => r.id)))} />
                  </th>
                )}
                <th className="text-left px-3 py-2.5">Merchant</th>
                <th className="text-left px-3 py-2.5">AWB / LR{orderType === 'B2B' && ' / Sub-AWB'}</th>
                {/* Named for whose number each one is. "Original / New" left the reader guessing
                    which side of the dispute they were looking at. */}
                <th className="text-right px-3 py-2.5">Merchant Chargeable Weight</th>
                <th className="text-right px-3 py-2.5">New Courier Weight</th>
                <th className="text-right px-3 py-2.5">Difference in Weight</th>
                <th className="text-left px-3 py-2.5">Weight Type</th>
                <th className="text-left px-3 py-2.5">Source</th>
                <th className="text-left px-3 py-2.5">Captured</th>
                <th className="text-left px-3 py-2.5">Status</th>
                {/* Internal only — a merchant decides nothing about what they are shown. */}
                {!isMerchant && <th className="text-left px-3 py-2.5">Show to merchant</th>}
              </tr>
            </thead>
            <tbody>
              {isLoading && (<tr><td colSpan={isMerchant ? 9 : 11} className="text-center py-10"><Loader2 size={20} className="mx-auto animate-spin text-primary" /></td></tr>)}
              {!isLoading && (data?.items ?? []).length === 0 && (<tr><td colSpan={isMerchant ? 9 : 11} className="text-center py-12 text-slate-400">No weight discrepancies for these filters.</td></tr>)}
              {(data?.items ?? []).map(r => (
                <tr key={r.id} className={`border-t border-slate-100 hover:bg-slate-50/60 ${selected.has(r.id) ? 'bg-emerald-50/50' : ''}`}>
                  {!isMerchant && (
                    <td className="px-3 py-2">
                      {isOpenRow(r) && <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />}
                    </td>
                  )}
                  <td className="px-3 py-2 text-xs font-medium text-slate-700">{r.merchantName || '—'}</td>
                  <td className="px-3 py-2 text-xs">
                    {/* The number now opens the shipment — a weight dispute is only ever settled by
                        looking at the order it belongs to, and `orderId` is already on the row, so
                        this costs no extra lookup. The link goes inside CopyText's `display` so the
                        copy button keeps working exactly as before. */}
                    <div className="font-mono">
                      <CopyText
                        value={r.awbNo || r.lrNo || ''}
                        iconSize={10}
                        silent
                        display={r.orderId ? (
                          <Link
                            to={`${r.orderType === 'B2B' ? '/orders-b2b' : '/orders-b2c'}/${r.orderId}`}
                            title="Open order details"
                            className="text-primary hover:underline">
                            {r.awbNo || r.lrNo || `#${r.orderId}`}
                          </Link>
                        ) : (
                          <span>{r.awbNo || r.lrNo || '—'}</span>
                        )}
                      />
                    </div>
                    {r.orderType === 'B2B' && r.subAwb && <div className="text-slate-500 font-mono">Sub: {r.subAwb}</div>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{Number(r.originalWeight).toFixed(3)} kg</td>
                  <td className="px-3 py-2 text-right font-mono font-medium">{Number(r.newWeight).toFixed(3)} kg</td>
                  <td className="px-3 py-2 text-right font-mono">
                    <span className="text-rose-600">+{Number(r.diffWeight).toFixed(3)} kg</span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span className={`px-2 py-0.5 rounded text-[10px] ${r.weightType === 'VOLUMETRIC' ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-700'}`}>{r.weightType}</span>
                  </td>
                  {/* Source alone ("UPLOAD") could not answer the question people actually ask —
                      WHICH of us loaded the sheet that re-priced this shipment. The name comes from
                      `capturedById`, which the upload has always stamped but nothing displayed. */}
                  <td className="px-3 py-2 text-xs">
                    <div className="uppercase">{r.source}</div>
                    {r.capturedByName
                      ? <div className="text-[10px] text-slate-500 normal-case" title={`Uploaded by ${r.capturedByName}`}>by {r.capturedByName}</div>
                      : r.source === 'TRACKING'
                        ? <div className="text-[10px] text-slate-400 normal-case">automatic</div>
                        : null}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{new Date(r.capturedAt).toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2 text-xs">
                    {/* Status first, then whatever action THIS viewer can take on it. A merchant
                        raises the dispute; only internal staff accept or reject it. */}
                    {(r as any).status === 'AUTO_ACCEPTED' ? (
                      // Distinct from a reviewed Accept on purpose: nobody agreed to this weight, a
                      // deadline passed. Anyone asked "who approved this charge?" months later needs
                      // to be able to see that from the row.
                      <span className="px-2 py-0.5 rounded bg-slate-200 text-slate-700 inline-flex items-center gap-1"
                        title={`No dispute was raised within 48 hours of this weight being captured, so it was accepted automatically.${(r as any).resolutionNote ? `
${(r as any).resolutionNote}` : ''}`}>
                        <CheckCircle2 size={11} /> Auto Accepted
                      </span>
                    ) : (r as any).status === 'ACCEPTED' || r.isAccepted ? (
                      <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Accepted</span>
                    ) : (r as any).status === 'REJECTED' ? (
                      <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-700" title={(r as any).resolutionNote || undefined}>Rejected</span>
                    ) : (r as any).status === 'PENDING' ? (
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800">Disputed</span>
                        {!isMerchant && (
                          <>
                            <button onClick={() => accept.mutate(r.id)} disabled={accept.isPending}
                              className="px-2 py-1 text-[11px] border border-emerald-200 text-emerald-700 rounded hover:bg-emerald-50 disabled:opacity-50">Accept</button>
                            <button onClick={() => setRejecting(r)}
                              className="px-2 py-1 text-[11px] border border-rose-200 text-rose-700 rounded hover:bg-rose-50">Reject</button>
                          </>
                        )}
                      </div>
                    ) : isMerchant ? (
                      // The deadline is shown WHILE the window is open, not announced after it shuts.
                      // A silent 48h clock that removes the right to dispute is a trap; a visible one
                      // is a deadline.
                      <div className="flex flex-col gap-1 items-start">
                        <button onClick={() => setDisputing(r)}
                          className="px-2 py-1 text-[11px] border border-primary text-primary rounded hover:bg-primary/5">Raise a dispute</button>
                        {(r as any).hoursLeftToDispute != null && (
                          <span className={`text-[10px] ${(r as any).hoursLeftToDispute <= 12 ? 'text-rose-600 font-medium' : 'text-slate-400'}`}>
                            {(r as any).hoursLeftToDispute <= 0
                              ? 'Closing now'
                              : `${(r as any).hoursLeftToDispute < 1
                                  ? `${Math.round((r as any).hoursLeftToDispute * 60)} min`
                                  : `${Math.floor((r as any).hoursLeftToDispute)} h`} left to dispute`}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button onClick={() => accept.mutate(r.id)} disabled={accept.isPending}
                          className="px-2 py-1 text-[11px] border border-emerald-200 text-emerald-700 rounded hover:bg-emerald-50 disabled:opacity-50">Accept</button>
                        <button onClick={() => setRejecting(r)}
                          className="px-2 py-1 text-[11px] border border-rose-200 text-rose-700 rounded hover:bg-rose-50">Reject</button>
                      </div>
                    )}
                    {/* History: who accepted / rejected it, and when (an auto-accept says so). */}
                    <ResolvedLine r={r} />
                    {/* Admin sees at a glance that photographs are attached and what was claimed. */}
                    {!isMerchant && (r as any).claimRaisedAt && (
                      <div className="text-[10px] text-slate-500 mt-1">
                        📎 evidence · {(r as any).claimLengthCm}×{(r as any).claimBreadthCm}×{(r as any).claimHeightCm}cm, {(r as any).claimWeightKg}kg
                      </div>
                    )}
                  </td>
                  {!isMerchant && (
                    <td className="px-3 py-2 whitespace-nowrap">
                      {/* Yes / No shown as the two choices, with the current one filled, so the
                          setting is readable without hovering. Until this is Yes the merchant does
                          not see the row at all — not on their list, summary, export, order page,
                          and they cannot accept or dispute it by id either. */}
                      {/* A non-Delhivery capture can never be released, so the switch is not
                          offered on one — an offer the server refuses is worse than no offer. */}
                      {r.canShowToMerchant === false ? (
                        <span className="text-[11px] text-slate-400"
                          title={`Weight disputes are shown to merchants on Delhivery shipments only. This one is ${r.courierName ?? 'another courier'}.`}>
                          Internal only · {r.courierName ?? 'not Delhivery'}
                        </span>
                      ) : (
                        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
                          {([true, false] as const).map((v) => {
                            const on = !!r.showToMerchant === v;
                            return (
                              <button key={String(v)} onClick={() => { if (!on) showToMerchant.mutate({ ids: [r.id], show: v }); }}
                                disabled={showToMerchant.isPending}
                                title={v ? 'Release this dispute to the merchant' : 'Keep this dispute internal'}
                                className={`px-2 py-0.5 text-[11px] transition disabled:opacity-50 ${on
                                  ? (v ? 'bg-emerald-600 text-white' : 'bg-slate-600 text-white')
                                  : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                                {v ? 'Yes' : 'No'}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {disputing && (
        <RaiseDisputeModal row={disputing} onClose={() => setDisputing(null)}
          onDone={() => { qc.invalidateQueries({ queryKey: ['weight'] }); qc.invalidateQueries({ queryKey: ['weight-sum'] }); }} />
      )}
      {rejecting && (
        <RejectDisputeModal row={rejecting} onClose={() => setRejecting(null)}
          onDone={() => { qc.invalidateQueries({ queryKey: ['weight'] }); qc.invalidateQueries({ queryKey: ['weight-sum'] }); }} />
      )}
      {showUpload && (
        <Modal isOpen={true} onClose={() => setShowUpload(false)} title={`Import Weight Discrepancies — ${orderType}`} subtitle="Download the sample file, fill it in, then import it (or paste the CSV)" maxWidth="2xl">
          <UploadDialog orderType={orderType} onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); qc.invalidateQueries({ queryKey: ['weight'] }); qc.invalidateQueries({ queryKey: ['weight-sum'] }); }} />
        </Modal>
      )}
    </div>
  );
}

/**
 * MERCHANT — raise a dispute with evidence, across every shipment of the same product.
 *
 * The product name drives a fan-out: the merchant photographs the product once and it is the same
 * product in every carton, so the claim is copied to all their still-open rows carrying that name.
 * The count is shown BEFORE submitting, because "this will raise a dispute on 47 shipments" is
 * something you want to see first, not discover afterwards.
 */
function RaiseDisputeModal({ row, onClose, onDone }: { row: Row; onClose: () => void; onDone: () => void }) {
  const [productName, setProductName] = useState('');
  const [note, setNote] = useState('');
  const [ev, setEv] = useState<any>({});
  const [uploading, setUploading] = useState(false);

  // Debounced so typing a product name doesn't fire a request per keystroke.
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebounced(productName.trim()), 400); return () => clearTimeout(t); }, [productName]);

  const { data: preview } = useQuery({
    enabled: debounced.length > 0,
    queryKey: ['wd-preview', row.id, debounced],
    queryFn: async () => (await api.get(`/weight/${row.id}/dispute-preview`, { params: { productName: debounced } })).data as { count: number; shipments: any[] },
  });

  const submit = useMutation({
    mutationFn: async () => (await api.post(`/weight/${row.id}/raise-dispute`, {
      productName: productName.trim(), note: note.trim(),
      length: Number(ev.length), breadth: Number(ev.breadth), height: Number(ev.height), weight: Number(ev.weight),
      imgLength: ev.imgLength, imgBreadth: ev.imgBreadth, imgHeight: ev.imgHeight, imgWeights: ev.imgWeights ?? [],
    })).data,
    onSuccess: (d: any) => {
      toast.success(`Dispute raised on ${d.shipments} shipment(s)${d.ticketId ? ` · ticket #${d.ticketId}` : ''}`);
      onDone(); onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not raise the dispute'),
  });

  const ready = productName.trim().length > 0 && evidenceComplete(ev) && !uploading;

  return (
    <Modal isOpen onClose={onClose} title="Raise a Weight Dispute" subtitle={`${row.awbNo || row.lrNo} · courier says ${Number(row.newWeight).toFixed(3)} kg vs your ${Number(row.originalWeight).toFixed(3)} kg`} maxWidth="4xl">
      <div className="space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
          Photograph the box being measured on each side and on the scale. The courier will not
          review a dispute without them, so all four photos are required.
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Product name <span className="text-rose-600">*</span></label>
          <input value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="Exactly as it appears on the order"
            className="input" />
          {debounced && preview && (
            <div className={`mt-1 text-xs ${preview.count > 1 ? 'text-emerald-700' : 'text-slate-500'}`}>
              {preview.count > 1
                ? <>These details will be applied to <b>{preview.count}</b> of your open shipments with this product.</>
                : <>This will apply to <b>this shipment only</b> — no other open case matches that product name.</>}
            </div>
          )}
        </div>

        <EvidenceForm value={ev} onChange={setEv} busyRef={setUploading} />

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Note (optional)</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="input" />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
          <button onClick={() => submit.mutate()} disabled={!ready || submit.isPending}
            title={!ready ? 'Enter the product name and upload all four photos first' : undefined}
            className="px-5 py-2 bg-primary text-white text-sm rounded font-medium disabled:opacity-50">
            {submit.isPending ? 'Submitting…' : `Raise dispute${preview && preview.count > 1 ? ` on ${preview.count} shipments` : ''}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * ADMIN — reject a merchant's evidenced claim, which requires the same evidence back.
 *
 * Rejecting a photographed claim with a bare "no" is an assertion, not an answer; the merchant gets
 * our measurements and photos by email so they can lay them against their own.
 */
function RejectDisputeModal({ row, onClose, onDone }: { row: any; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState('');
  const [ev, setEv] = useState<any>({});
  const [uploading, setUploading] = useState(false);
  const merchantRaised = !!row.claimRaisedAt;

  const submit = useMutation({
    mutationFn: async () => (await api.post(`/weight/${row.id}/reject`, {
      note: note.trim(),
      proof: merchantRaised ? {
        length: Number(ev.length), breadth: Number(ev.breadth), height: Number(ev.height), weight: Number(ev.weight),
        imgLength: ev.imgLength, imgBreadth: ev.imgBreadth, imgHeight: ev.imgHeight, imgWeights: ev.imgWeights ?? [],
      } : undefined,
    })).data,
    onSuccess: () => { toast.success('Dispute rejected — the merchant has been emailed'); onDone(); onClose(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not reject'),
  });

  const ready = note.trim().length > 0 && (!merchantRaised || (evidenceComplete(ev) && !uploading));

  return (
    <Modal isOpen onClose={onClose} title="Reject Weight Dispute" subtitle={row.awbNo || row.lrNo} maxWidth="4xl">
      <div className="space-y-4">
        {merchantRaised ? (
          <>
            <div className="bg-rose-50 border border-rose-200 rounded p-3 text-xs text-rose-900">
              The merchant evidenced this claim with photographs. To reject it you must supply your
              own measurement and photos — they are emailed to the merchant with the rejection.
            </div>
            <div className="bg-slate-50 border rounded p-3 text-xs">
              <b>Merchant claimed:</b> {row.claimLengthCm} × {row.claimBreadthCm} × {row.claimHeightCm} cm ·{' '}
              {row.claimWeightKg} kg{row.productName ? <> · product <b>{row.productName}</b></> : null}
            </div>
            <EvidenceForm value={ev} onChange={setEv} busyRef={setUploading} />
          </>
        ) : (
          <div className="bg-slate-50 border rounded p-3 text-xs text-slate-600">
            Nobody has disputed this row, so no counter-evidence is required — a reason is enough.
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Reason <span className="text-rose-600">*</span></label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="input"
            placeholder="What did you measure, and why does the courier's weight stand?" />
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
          <button onClick={() => submit.mutate()} disabled={!ready || submit.isPending}
            className="px-5 py-2 bg-rose-600 text-white text-sm rounded font-medium disabled:opacity-50">
            {submit.isPending ? 'Rejecting…' : 'Reject & email merchant'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * The dimension + weight evidence form, used by BOTH sides of a dispute.
 *
 * The merchant fills it in to raise the claim; the admin fills in the identical form to reject it.
 * One component on purpose — the whole point of requiring counter-evidence is that it can be laid
 * against the merchant's photographs field for field, which only holds if the two are the same shape.
 *
 * Every field is mandatory: a courier will not entertain "the box is smaller than you say" without
 * a photograph of the box being measured, so an unevidenced dispute costs the merchant time and
 * changes nothing. The submit button stays disabled until all four photos are uploaded.
 */
function EvidenceForm({ value, onChange, busyRef }: { value: any; onChange: (v: any) => void; busyRef?: (b: boolean) => void }) {
  const [uploading, setUploading] = useState<string | null>(null);
  const set = (k: string, v: any) => onChange({ ...value, [k]: v });

  const upload = async (key: string, file: File, multi = false) => {
    setUploading(key); busyRef?.(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/uploads?category=weight-dispute', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const ref = r.data?.ref;
      const url = r.data?.url;
      if (!ref) throw new Error('Upload returned no reference');
      if (multi) {
        onChange({ ...value, imgWeights: [...(value.imgWeights ?? []), ref], imgWeightUrls: [...(value.imgWeightUrls ?? []), url] });
      } else {
        onChange({ ...value, [key]: ref, [`${key}Url`]: url });
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? e?.message ?? 'Upload failed');
    } finally { setUploading(null); busyRef?.(false); }
  };

  const PhotoField = ({ k, label }: { k: string; label: string }) => (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label} photo <span className="text-rose-600">*</span></label>
      {value[`${k}Url`] ? (
        <div className="relative">
          <img src={value[`${k}Url`]} alt={label} className="w-full h-24 object-cover rounded border border-slate-200" />
          <button type="button" onClick={() => onChange({ ...value, [k]: '', [`${k}Url`]: '' })}
            className="absolute top-1 right-1 bg-white/90 rounded px-1.5 text-xs border">✕</button>
        </div>
      ) : (
        <label className={`flex items-center justify-center h-24 rounded border-2 border-dashed cursor-pointer text-xs ${uploading === k ? 'border-slate-300 text-slate-400' : 'border-slate-300 text-slate-500 hover:border-primary hover:text-primary'}`}>
          {uploading === k ? 'Uploading…' : '+ Add photo'}
          <input type="file" accept="image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(k, f); e.currentTarget.value = ''; }} />
        </label>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Length (cm) <span className="text-rose-600">*</span></label>
          <input type="number" min="0.1" step="0.1" value={value.length ?? ''} onChange={(e) => set('length', e.target.value)} className="input input-num" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Breadth (cm) <span className="text-rose-600">*</span></label>
          <input type="number" min="0.1" step="0.1" value={value.breadth ?? ''} onChange={(e) => set('breadth', e.target.value)} className="input input-num" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Height (cm) <span className="text-rose-600">*</span></label>
          <input type="number" min="0.1" step="0.1" value={value.height ?? ''} onChange={(e) => set('height', e.target.value)} className="input input-num" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Weight (kg) <span className="text-rose-600">*</span></label>
          <input type="number" min="0.001" step="0.001" value={value.weight ?? ''} onChange={(e) => set('weight', e.target.value)} className="input input-num" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <PhotoField k="imgLength" label="Length" />
        <PhotoField k="imgBreadth" label="Breadth" />
        <PhotoField k="imgHeight" label="Height" />
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Weight photo(s) <span className="text-rose-600">*</span></label>
          <div className="flex flex-wrap gap-1">
            {(value.imgWeightUrls ?? []).map((u: string, i: number) => (
              <div key={i} className="relative">
                <img src={u} alt="" className="w-16 h-16 object-cover rounded border border-slate-200" />
                <button type="button"
                  onClick={() => onChange({
                    ...value,
                    imgWeights: (value.imgWeights ?? []).filter((_: any, j: number) => j !== i),
                    imgWeightUrls: (value.imgWeightUrls ?? []).filter((_: any, j: number) => j !== i),
                  })}
                  className="absolute -top-1 -right-1 bg-white rounded-full border w-4 h-4 text-[10px] leading-none">✕</button>
              </div>
            ))}
            <label className="w-16 h-16 flex items-center justify-center rounded border-2 border-dashed border-slate-300 cursor-pointer text-[10px] text-slate-500 hover:border-primary">
              {uploading === 'imgWeights' ? '…' : '+ Add'}
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) upload('imgWeights', f, true); e.currentTarget.value = ''; }} />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

/** All four photo slots filled and all four numbers > 0 — the submit gate for both sides. */
function evidenceComplete(v: any): boolean {
  const nums = ['length', 'breadth', 'height', 'weight'].every((k) => Number(v?.[k]) > 0);
  const imgs = ['imgLength', 'imgBreadth', 'imgHeight'].every((k) => !!v?.[k]);
  return nums && imgs && (v?.imgWeights?.length ?? 0) > 0;
}

function Card({ label, value, accent }: { label: string; value: string | number; accent: string }) {
  const tone = { indigo: 'bg-indigo-50 border-indigo-200', amber: 'bg-amber-50 border-amber-200', slate: 'bg-white border-slate-200', rose: 'bg-rose-50 border-rose-200' } as any;
  return (
    <div className={`p-3 rounded-lg border ${tone[accent]}`}>
      <div className="text-xs text-slate-500 uppercase">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}
function BreakdownCard({ title, rows }: { title: string; rows: Array<{ label: string; count: number; kg: number }> }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3">
      <div className="text-xs uppercase text-slate-500 mb-2">{title}</div>
      <table className="w-full text-sm">
        <thead><tr className="text-xs text-slate-500"><th className="text-left">Label</th><th className="text-right">Count</th><th className="text-right">Total kg</th></tr></thead>
        <tbody>
          {rows.filter(r => r.count > 0).map(r => (
            <tr key={r.label} className="border-t border-slate-100"><td className="py-1">{r.label}</td><td className="py-1 text-right font-mono">{r.count}</td><td className="py-1 text-right font-mono text-rose-600">+{Number(r.kg).toFixed(3)}</td></tr>
          ))}
          {rows.every(r => r.count === 0) && <tr><td colSpan={3} className="text-center text-slate-400 py-2 text-xs">No data</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function UploadDialog({ orderType, onClose, onDone }: { orderType: 'B2C' | 'B2B'; onClose: () => void; onDone: () => void }) {
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState<any>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const idLabel = orderType === 'B2C' ? 'AWB' : 'LR';
  const sample = sampleCsv(orderType);
  const rowCount = parseCsv(text).length;

  // Read a picked .csv into the textarea, so what gets uploaded is always what is on screen.
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    const name = f.name.toLowerCase();
    if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      toast.error('Please import a CSV file, not an Excel file. Open your sheet in Excel and use File → Save As → "CSV (Comma delimited)", then import that file.');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    const r = new FileReader();
    r.onload = (ev) => { setText(String(ev.target?.result ?? '')); setFileName(f.name); setResult(null); };
    r.onerror = () => toast.error('Could not read that file. Please try again.');
    r.readAsText(f);
  };

  const upload = useMutation({
    mutationFn: async () => {
      const rows = parseCsv(text);
      if (rows.length === 0) throw new Error('No rows found — the file needs a header row plus at least one data row.');
      return (await api.post('/weight/upload', { orderType, rows })).data;
    },
    onSuccess: (data: any) => { setResult(data); toast.success(`Processed ${data.ok}, ${data.failed} failed`); if (data.failed === 0) setTimeout(onDone, 1200); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? 'Upload failed'),
  });

  return (
    <div className="space-y-4">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
        <strong>CSV columns:</strong> <code>{idLabel}</code>, <code>newWeight</code> (required, kg). Optional: <em>originalWeight</em> (taken from the order when blank), <em>volumetricWeight</em>, <em>weightType</em> (DEAD | VOLUMETRIC), <em>reason</em>{orderType === 'B2B' && <>, <em>subAwb</em></>}.
        <div className="text-xs text-emerald-800 mt-1">Column headers are matched case-insensitively, so <code>AWB No</code>, <code>New Weight</code> etc. all work. newWeight must be greater than the booked weight — that difference is the dispute.</div>
        <button onClick={() => downloadSample(orderType)}
          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700">
          <Download size={12} /> Download sample file ({orderType})
        </button>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Import CSV file</label>
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile}
          className="text-sm w-full file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:text-sm file:bg-primary file:text-white file:cursor-pointer cursor-pointer" />
        {fileName && <div className="text-xs text-emerald-700 mt-1 flex items-center gap-1"><FileUp size={11} /> {fileName} — {rowCount} row(s) loaded</div>}
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Or paste CSV content</label>
        <textarea value={text} onChange={(e) => { setText(e.target.value); setFileName(''); }} rows={8} placeholder={sample} className="input font-mono text-xs w-full" />
      </div>
      {result && (
        <div className="rounded-lg border">
          <div className="grid grid-cols-3 divide-x text-center">
            <div className="p-3"><div className="text-xs text-slate-500">Total</div><div className="text-xl font-bold">{(result.ok ?? 0) + (result.failed ?? 0)}</div></div>
            <div className="p-3 bg-emerald-50"><div className="text-xs text-emerald-700">OK</div><div className="text-xl font-bold text-emerald-700">{result.ok}</div></div>
            <div className="p-3 bg-rose-50"><div className="text-xs text-rose-700">Failed</div><div className="text-xl font-bold text-rose-700">{result.failed}</div></div>
          </div>
          {result.errors?.length > 0 && (
            <div className="p-3 bg-amber-50 text-xs">
              <div className="font-medium mb-1">Errors:</div>
              <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                {result.errors.map((e: any, i: number) => <li key={i}>Row {e.row} {e.awb && <b>({e.awb})</b>}: {e.error}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
        <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Close</button>
        <button onClick={() => upload.mutate()} disabled={!text.trim() || upload.isPending}
          className="px-5 py-2 bg-primary text-white text-sm rounded font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
          {upload.isPending ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Process
        </button>
      </div>
    </div>
  );
}

// Map a spreadsheet header onto the EXACT key the backend reads (weight.service.ts UploadRow).
// This mapping is not cosmetic: the backend reads camelCase (`newWeight`), so a header merely
// lower-cased to `newweight` arrives as undefined and every row fails "newWeight must be > 0".
const HEADER_ALIASES: Record<string, string> = {
  awb: 'awb', awbno: 'awb', awbnumber: 'awb', waybill: 'awb', waybillno: 'awb',
  lr: 'awb', lrno: 'awb', lrnumber: 'awb', masterawb: 'awb',
  subawb: 'subAwb', childawb: 'subAwb', boxawb: 'subAwb',
  originalweight: 'originalWeight', oldweight: 'originalWeight', bookedweight: 'originalWeight', chargedweight: 'originalWeight', chargeableweight: 'originalWeight',
  newweight: 'newWeight', actualweight: 'newWeight', revisedweight: 'newWeight', courierweight: 'newWeight', reweight: 'newWeight',
  volumetricweight: 'volumetricWeight', volweight: 'volumetricWeight', volumeweight: 'volumetricWeight',
  weighttype: 'weightType', type: 'weightType',
  reason: 'reason', remark: 'reason', remarks: 'reason', note: 'reason', notes: 'reason',
};
/** `Charged Weight (kgs)` → `chargedweight` → looked up in HEADER_ALIASES. */
function canonicalHeader(h: string): string {
  const squashed = h.toLowerCase().replace(/\(.*?\)/g, '').replace(/[^a-z0-9]/g, '');
  return HEADER_ALIASES[squashed] ?? h.trim();
}


/** Weight-discrepancy rows, via the shared quote-aware tokeniser (see utils/csv-helper). */
function parseCsv(text: string): Array<Record<string, string>> {
  return parseDelimited(text, { mapHeader: canonicalHeader }).rows;
}
